import React, { useState, useMemo, useEffect } from "react";
import OperationsHub from "../OperationsHub";
import Loading from "../Loading";
import NoContent from "../NoContent";
import ContentHeader from "../ContentHeader";
import InteractivePane from "../InteractivePane";

import data from "../../../../_data/operations-hub.json";

export default function ProjectPipeline() {
  const [tableData, setTableData] = useState(null);

  useEffect(() => {
    let visibleProjects = [];

    if (data && data.projects) {
      visibleProjects = data.projects.filter((p) => p.isVisible);
    }

    setTableData(visibleProjects);
  }, []);

  const sortedTimeline = useMemo(() => formatToTimelineItem(tableData ?? []), [tableData]);

  if (!tableData) return <Loading />;
  if (tableData.length < 1) return <NoContent message={"No projects in queue."} />;

  return (
    <>
      <OperationsHub />

      <ContentHeader
        title="Project Pipeline"
        caption="A simple view of JumpStart engagements across stages. Mirrors ADO/Grafana, but stays readable for quick standups."
      />

      <InteractivePane>
        <div className="timeline-queue">
          <Timeline sortedProjects={sortedTimeline} colorTheme="color-coded" />
        </div>
      </InteractivePane>
    </>
  );
}

function Timeline({ sortedProjects, colorTheme }) {
  const [groupedProjects, setGroupedProjects] = useState(null);

  useEffect(() => {
    const sectionCollection = new Map();
    let groupCollection = new Map();
    let prevSection = "";

    for (const p of sortedProjects) {
      if (p.section != prevSection.valueOf()) groupCollection = new Map();

      const group = prevSection.valueOf() === p.section ? (groupCollection.get(p.group) ?? []) : [];
      const section = sectionCollection.get(p.section) ?? new Map();

      group.push(p.body);
      groupCollection.set(p.group, group);

      section.set(p.group, groupCollection.get(p.group) ?? []);
      sectionCollection.set(p.section, section);

      prevSection = p.section;
    }

    setGroupedProjects(sectionCollection);
  }, []);

  if (!groupedProjects) return <Loading />;

  const mappedProjects = groupedProjects
    .keys()
    .map((p) => {
      const timelineTheme = createTheme(colorTheme ?? "");

      return (
        <div className="timeline-entry" key={p}>
          {/* Section layer */}
          <div className="section-layer">
            <h1 style={timelineTheme.section}>{p}</h1>
          </div>

          {/* Group layer */}
          <div className="group-layer">
            {groupedProjects
              .get(p)
              ?.keys()
              .map((group) => {
                /* Body Layer */
                return (
                  <div className="body-layer" style={timelineTheme.group} key={group}>
                    <h2>{group}</h2>
                    {groupedProjects
                      .get(p)
                      ?.get(group)
                      ?.map((entry, i) => {
                        return (
                          <p style={timelineTheme.body} key={i}>
                            {entry}
                          </p>
                        );
                      })}
                  </div>
                );
              })}
          </div>
        </div>
      );
    })
    .toArray();

  return mappedProjects;
}

function createTheme(themeStyle) {
  const theme = {};

  const convertRGBtoHex = (r, g, b) => {
    let hexVal = "#";

    for (const val of [r, g, b]) {
      hexVal += val.toString(16).toUpperCase();
    }

    return hexVal;
  };

  const generateShades = () => {
    const max = 255;
    const min = 0;

    const r = Math.floor(Math.random() * (max - min + 1)) + min;
    const g = Math.floor(Math.random() * (max - min + 1)) + min;
    const b = Math.floor(Math.random() * (max - min + 1)) + min;

    return {
      hue: convertRGBtoHex(r, g, b),
      light: convertRGBtoHex(
        Math.floor(r + 0.2 * (255 - r)),
        Math.floor(g + 0.2 * (255 - g)),
        Math.floor(b + 0.2 * (255 - b)),
      ),
      lighter: convertRGBtoHex(
        Math.floor(r + 0.5 * (255 - r)),
        Math.floor(g + 0.5 * (255 - g)),
        Math.floor(b + 0.5 * (255 - b)),
      ),
      dark: convertRGBtoHex(
        Math.floor(r + 0.7 * (255 - r)),
        Math.floor(g + 0.7 * (255 - g)),
        Math.floor(b + 0.7 * (255 - b)),
      ),
      darker: convertRGBtoHex(Math.floor(r * 0.9), Math.floor(g * 0.9), Math.floor(b * 0.9)),
    };
  };

  switch (themeStyle) {
    /* CGI GRADIENT THEME */
    case "cgi":
      theme.section = {
        background: "linear-gradient(to right, #dc2626, #c026d3, #9333ea)",
        color: "#fff",
      };
      theme.group = {
        backgroundColor: "#F2F1F9",
        borderColor: "#9333ea",
        color: "#3A2679",
      };
      theme.body = {
        backgroundColor: "#AFA3D8",
        color: "#333333",
      };
      break;

    /* COLOR-CODED */
    case "color-coded": {
      const generatedShades = generateShades();

      theme.section = {
        backgroundColor: generatedShades.light,
        color: "#000",
      };
      theme.group = {
        borderColor: generatedShades.lighter,
        backgroundColor: generatedShades.dark,
        color: "#000",
      };
      theme.body = {
        backgroundColor: generatedShades.lighter,
        color: "#000",
      };
      break;
    }

    /* DEFAULT THEME */
    default:
      theme.section = {
        background: "#5236AB",
        color: "#fff",
      };
      theme.group = {
        borderColor: "#AFA3D8",
        backgroundColor: "#F2F1F9",
        color: "#3A2679",
      };
      theme.body = {
        backgroundColor: "#AFA3D8",
        color: "#333333",
      };
      break;
  }

  return theme;
}

function formatToTimelineItem(projects) {
  const queueItems = [];

  const sortedPipeline = projects
    .sort((entry, nextEntry) => entry.initiatedOn.localeCompare(nextEntry.initiatedOn))
    .filter((entry) => ["ON HOLD", "NEW", "IN PROGRESS"].includes(entry.status));

  for (const project of sortedPipeline) {
    const dateParts = project.initiatedOn.replace(/\s\d{2}:\d{2}/g, "").split("-");
    const parsedDate = new Date(
      parseInt(dateParts[0]),
      parseInt(dateParts[1]) - 1,
      parseInt(dateParts[2]),
    );

    const year = parsedDate.getFullYear();
    const month = new Date(year, parsedDate.getMonth()).toLocaleDateString("default", {
      month: "long",
    });
    const quarter = Math.ceil((parsedDate.getMonth() + 1) / 3);
    const section = `${year} Q${quarter}`;

    queueItems.push({
      section: section,
      group: month,
      body: project.title,
    });
  }

  return queueItems;
}
